import { DateTime, Duration } from 'luxon'
import { ChartDateShortcut, chartDateShortcuts, dateShortcutDetails } from '../types'
import { useSuspenseQuery } from '@tanstack/react-query'
import { analyticsOverviewQueryOptions } from '../../../queries'
import { match, P } from 'ts-pattern'
import { recursiveSum } from '~/utils'
import { groupBy } from 'lodash'
import { z } from 'zod'

export const defaultDataKeys = [
  'Total Users',
  'New Users',
  'Returning Users',
  'Sessions',
  'Bot Messages',
  'User Messages',
  'Total Messages',
]

const ratioMetricSchema = z.object({
  numerator: z.number(),
  denominator: z.number(),
})

export type RatioMetric = z.infer<typeof ratioMetricSchema>

export function isRatioMetric(value: unknown): value is RatioMetric {
  return ratioMetricSchema.safeParse(value).success
}

type AnalyticsData = {
  timestamp: Date & Record<keyof typeof defaultDataKeys, number>
} & Omit<Record<string, number | RatioMetric>, 'timestamp'>

type Props = {
  botId: string
  workspaceId: string
  children?: React.ReactNode | ((props: { data: AnalyticsData[]; resolution: Resolution }) => React.ReactNode)
  dateShortcut?: ChartDateShortcut
  startDate?: string
  endDate?: string
}
export const ChartDataProvider = ({
  botId,
  workspaceId,
  children,
  dateShortcut = 'lastMonth',
  startDate,
  endDate,
}: Props) => {
  const shortcut = chartDateShortcuts.includes(dateShortcut) ? dateShortcut : 'lastMonth'
  const start = startDate
    ? new Date(startDate)
    : DateTime.now().minus({ days: dateShortcutDetails[shortcut].days }).toJSDate()
  const end = endDate ? new Date(endDate) : new Date()
  const analytics = useSuspenseQuery(
    analyticsOverviewQueryOptions({ botId, workspaceId, startDate: start, endDate: end })
  ).data.records.map(({ endDateTimeUtc, startDateTimeUtc: _, eventTypes, customEvents, ...record }) => {
    const {
      'webchat:messageFeedbackNegative': webchatFeebackNegative,
      'webchat:messageFeedbackPositive': webchatFeedbackPositive,
      ...otherCustomEvents
    } = customEvents

    return {
      timestamp: DateTime.fromISO(endDateTimeUtc).toJSDate(),
      'Total Users': record.newUsers + record.returningUsers,
      'New Users': record.newUsers,
      'Returning Users': record.returningUsers,
      Sessions: record.sessions,
      'Bot Messages': record.botMessages,
      'User Messages': record.userMessages,
      'Total Messages': record.botMessages + record.userMessages,
      'Avg. Bot Messages per Session': {
        numerator: record.botMessages,
        denominator: record.sessions,
      },
      'Avg. User Messages per Session': {
        numerator: record.userMessages,
        denominator: record.sessions,
      },
      'Avg. Sessions per User': {
        numerator: record.sessions,
        denominator: record.newUsers + record.returningUsers,
      },
      ...eventTypes,
      'Webchat: Message Feedback - Positive': webchatFeedbackPositive ?? 0,
      'Webchat: Message Feedback - Negative': webchatFeebackNegative ?? 0,
      ...otherCustomEvents,
      'LLM Calls': record.llm.calls,
      'LLM Errors': record.llm.errors,
      'LLM Error Rate %': record.llm.calls > 0 ? trimDecimals(record.llm.errors / record.llm.calls, 2) : 0,
      'LLM Input Tokens Processed': record.llm.inputTokens,
      'LLM Output Tokens Generated': record.llm.outputTokens,
      'LLM Latency (ms) - Average': trimDecimals(record.llm.latency.mean, 0),
      'LLM Latency (ms) - Standard Deviation': trimDecimals(record.llm.latency.sd, 1),
      'LLM Latency (ms) - Minimum': record.llm.latency.min,
      'LLM Latency (ms) - Maximum': record.llm.latency.max,
      'LLM Tokens Per Second - Average': trimDecimals(record.llm.tokensPerSecond.mean, 1),
      'LLM Tokens Per Second - Standard Deviation': trimDecimals(record.llm.tokensPerSecond.sd, 2),
      'LLM Tokens Per Second - Minimum': trimDecimals(record.llm.tokensPerSecond.min, 1),
      'LLM Tokens Per Second - Maximum': trimDecimals(record.llm.tokensPerSecond.max, 1),
      'LLM Cost - Sum USD $': trimDecimals(record.llm.cost.sum, 6),
      'LLM Cost - Average USD $ (per call)': trimDecimals(record.llm.cost.mean, 7),
      'LLM Cost - Standard Deviation USD $ (per call)': trimDecimals(record.llm.cost.sd, 8),
      'LLM Cost - Minimum USD $ (per call)': trimDecimals(record.llm.cost.min, 8),
      'LLM Cost - Maximum USD $ (per call)': trimDecimals(record.llm.cost.max, 7),
    }
  }) as any as AnalyticsData[]

  return (
    <>
      {' '}
      {typeof children === 'function'
        ? children({
            data: fillGapsWithZeros(analytics, start, end),
            resolution: getResolution(start, end),
          })
        : children}
    </>
  )
}

/**
 * Trims the number of decimals of a number in a safe way, as toFixed() returns a string which isn't always handled correctly by the charts.
 */
function trimDecimals(value: number, decimals: number) {
  return Number(value.toFixed(decimals))
}

function fillGapsWithZeros(data: AnalyticsData[], startDate: Date, endDate: Date): AnalyticsData[] {
  const [first] = data
  const last = data[data.length - 1]

  if (!first || !last) {
    return data
  }

  const keys = [...new Set([...data.flatMap((d) => Object.keys(d))])]

  const resolution = getResolution(startDate, endDate)

  // Sort data by timestamp
  data.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())

  //if the first element in data is not older than startDate plus 1 resolution, add a zero filled element to the beginning of the data
  if (
    DateTime.fromJSDate(first.timestamp)
      .minus({ [resolution]: 1 })
      .toJSDate() > startDate
  ) {
    data.unshift(createZeroFilledData(startDate, Object.keys(first), first))
  }

  //if the last element in data is not older that endDate minus 1 resolution, add a zero filled element to the end of the data
  if (
    DateTime.fromJSDate(last.timestamp)
      .plus({ [resolution]: 1 })
      .toJSDate() < endDate
  ) {
    data.push(createZeroFilledData(endDate, keys, first))
  }

  const roundedTimestampsData: AnalyticsData[] = data.map((d) => {
    const dt = DateTime.fromJSDate(d.timestamp)
    return { ...d, timestamp: dt.startOf(resolution).toJSDate() } as AnalyticsData
  })

  // This is temporary while the backend is fixed
  const groupedByTimestamp = groupBy(roundedTimestampsData, (d) => d.timestamp.getTime())

  // This is temporary while the backend is fixed
  const combinedData: AnalyticsData[] = Object.values(groupedByTimestamp).map((group) => {
    return group.reduce((acc, curr) => {
      return { ...recursiveSum(acc, curr), timestamp: acc.timestamp }
    })
  })

  const filledData = combinedData.reduce((acc: AnalyticsData[], curr, idx, src) => {
    // Always add the current item
    acc.push(curr)

    if (idx < src.length - 1) {
      const next = src[idx + 1]
      if (!next) {
        return acc
      }
      let currentTime = DateTime.fromJSDate(curr.timestamp)
      const nextTime = DateTime.fromJSDate(next.timestamp)
      // Check if there is a gap
      while (
        Duration.fromMillis(nextTime.toMillis() - currentTime.toMillis()) >= Duration.fromObject({ [resolution]: 2 })
      ) {
        currentTime = currentTime.plus({ [resolution]: 1 })
        acc.push(createZeroFilledData(currentTime.toJSDate(), keys, first))
      }
    }

    return acc
  }, [])
  return filledData
}

function createZeroFilledData(timestamp: Date, keys: string[], sampleData: AnalyticsData): AnalyticsData {
  const zeroFilledData = { timestamp } as AnalyticsData
  for (const key of keys) {
    if (key === 'timestamp') {
      continue
    }

    if (key in sampleData) {
      const existingValue = sampleData[key]
      if (isRatioMetric(existingValue)) {
        zeroFilledData[key] = { numerator: 0, denominator: 0 }
        continue
      }
    }

    zeroFilledData[key] = 0
  }
  return zeroFilledData
}

export type Resolution = 'hour' | 'day' | 'week' | 'month'
function getResolution(startDate: Date, endDate: Date): Resolution {
  const daySpan = getDaySpan(startDate, endDate)
  return match(daySpan)
    .with(P.number.gt(120), () => 'month' as const)
    .with(P.number.gt(60), () => 'week' as const)
    .with(P.number.gt(3), () => 'day' as const)
    .otherwise(() => 'hour' as const)
}

function getDaySpan(startDate: Date, endDate: Date) {
  return DateTime.fromJSDate(endDate).diff(DateTime.fromJSDate(startDate), 'days').days
}
